import { getOwner } from '@ember/owner';
import Service from '@ember/service';
import type { Dayjs } from 'dayjs';
import type {
  TotalRevenueItem,
  TotalRevenueSegmentationBreakdownItem,
} from 'lumen/models';
import Item from 'lumen/utils/datamodels/total-revenue/item';
import ReferenceItem from 'lumen/utils/datamodels/total-revenue/reference-item';

function notNull<T>(value: T | null): value is T {
  return value !== null;
}

interface ReferenceLike {
  referenceFromDate: Dayjs;
  referenceType: string;
}

/**
 * Service used for calculating the sum of multiple Total Revenue Ember Data models.
 * This can be used by e.g. the portfolio table to calculate subtotals for grouped rows.
 */
export default class TotalRevenueSummationService extends Service {
  #groupReferenceItemsByPeriod<RefItem extends ReferenceLike>(
    items: RefItem[],
  ): Record<string, RefItem[]> {
    return items.reduce(
      (acc, referenceItem) => {
        if (!acc[referenceItem.referenceType]) {
          acc[referenceItem.referenceType] = [];
        }

        acc[referenceItem.referenceType]?.push(referenceItem);

        return acc;
      },
      {} as Record<string, RefItem[]>,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  #summateData<I, Key extends keyof I>(items: I[], keys: readonly Key[]) {
    const emptyItem = keys.reduce(
      (acc, key) => {
        acc[key] = 0;
        return acc;
      },
      {} as Record<Key, number>,
    );

    return items.reduce((acc, item) => {
      keys.forEach((key) => {
        const value = item[key];
        if (typeof value !== 'number') {
          return;
        }

        acc[key] += value;
      });

      return acc;
    }, emptyItem);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  #summateReferenceItemData<I extends ReferenceLike, Key extends keyof I>(
    items: I[],
    keys: readonly Key[],
  ) {
    const firstItem = items[0];
    if (!firstItem) {
      return null;
    }

    return {
      referenceType: firstItem.referenceType,
      referenceFromDate: firstItem.referenceFromDate,
      ...this.#summateData(items, keys),
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getSummatedItem(items: TotalRevenueItem[]) {
    const propertiesToSummate = [
      'totalRevenue',
      'totalRoomRevenue',
      'totalNonRoomRevenue',
      'capacity',
    ] as const;

    const referenceItemsByPeriod = this.#groupReferenceItemsByPeriod(
      items.flatMap((i) => [...i.referenceValues]),
    );

    const referenceValues = Object.values(referenceItemsByPeriod)
      .map((referenceItems) =>
        this.#summateReferenceItemData(referenceItems, propertiesToSummate),
      )
      .filter(notNull)
      .map((referenceItem) =>
        ReferenceItem.create(getOwner(this), referenceItem),
      );

    const summatedItem = {
      ...this.#summateData(items, propertiesToSummate),
      referenceValues,
    };

    return Item.create(getOwner(this), summatedItem);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getSummatedSegmentationBreakdownItem(
    items: TotalRevenueSegmentationBreakdownItem[],
  ) {
    const propertiesToSummate = [
      'totalRevenue',
      'totalRoomRevenue',
      'totalNonRoomRevenue',
      'nrOfRooms',
    ] as const;

    const referenceItemsByPeriod = this.#groupReferenceItemsByPeriod(
      items.flatMap((i) => [...i.referenceValues]),
    );

    const referenceValues = Object.values(referenceItemsByPeriod)
      .map((referenceItems) =>
        this.#summateReferenceItemData(referenceItems, propertiesToSummate),
      )
      .filter(notNull)
      .map((referenceItem) =>
        ReferenceItem.create(getOwner(this), referenceItem),
      );

    const summatedItem = {
      ...this.#summateData(items, propertiesToSummate),
      referenceValues,
    };

    return Item.create(getOwner(this), summatedItem);
  }
}

declare module '@ember/service' {
  interface Registry {
    'total-revenue/summation': TotalRevenueSummationService;
  }
}
